/**
 * UTM Parameter Tracker - Optimized for Subscription Page Only
 * Extracts UTM parameters from URL for CleverTap events
 * 
 * UTM params are stored in sessionStorage on first capture
 * and retrieved from there on subsequent pages (after navigation)
 */

interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_adset?: string;
  utm_ad?: string;
  utm_id?: string;
  utm_content?: string;
  utm_term?: string;
  fbclid?: string;
  gclid?: string;
}

// Session storage keys for UTM params
const UTM_STORAGE_KEYS = {
  utm_source: 'utm_source',
  utm_medium: 'utm_medium',
  utm_campaign: 'utm_campaign',
  utm_adset: 'utm_adset',
  utm_ad: 'utm_ad',
  utm_id: 'utm_id',
  utm_content: 'utm_content',
  utm_term: 'utm_term',
  fbclid: 'utm_fbclid',
  gclid: 'utm_gclid'
};

/**
 * Extract and store UTM parameters from URL
 * Call this on the landing page to capture UTM params
 * Params are stored in sessionStorage for retrieval on subsequent pages
 */
export const extractAndStoreUTMParams = (): void => {
  const urlParams = new URLSearchParams(window.location.search);
  
  const utmSource = urlParams.get('utm_source');
  const utmMedium = urlParams.get('utm_medium');
  const utmCampaign = urlParams.get('utm_campaign');
  const utmAdset = urlParams.get('utm_adset');
  const utmAd = urlParams.get('utm_ad');
  const utmId = urlParams.get('utm_id');
  const utmContent = urlParams.get('utm_content');
  const utmTerm = urlParams.get('utm_term');
  const fbclid = urlParams.get('fbclid');
  const gclid = urlParams.get('gclid');
  
  // Only store if params exist in URL (don't overwrite with empty values)
  if (utmSource) sessionStorage.setItem(UTM_STORAGE_KEYS.utm_source, utmSource);
  if (utmMedium) sessionStorage.setItem(UTM_STORAGE_KEYS.utm_medium, utmMedium);
  if (utmCampaign) sessionStorage.setItem(UTM_STORAGE_KEYS.utm_campaign, utmCampaign);
  if (utmAdset) sessionStorage.setItem(UTM_STORAGE_KEYS.utm_adset, utmAdset);
  if (utmAd) sessionStorage.setItem(UTM_STORAGE_KEYS.utm_ad, utmAd);
  if (utmId) sessionStorage.setItem(UTM_STORAGE_KEYS.utm_id, utmId);
  if (utmContent) sessionStorage.setItem(UTM_STORAGE_KEYS.utm_content, utmContent);
  if (utmTerm) sessionStorage.setItem(UTM_STORAGE_KEYS.utm_term, utmTerm);
  if (fbclid) sessionStorage.setItem(UTM_STORAGE_KEYS.fbclid, fbclid);
  if (gclid) sessionStorage.setItem(UTM_STORAGE_KEYS.gclid, gclid);
};

/**
 * Extract UTM parameters - first checks URL, then sessionStorage
 * This ensures UTM params are available on first page load and subsequent pages
 * Returns empty object if no params found
 */
export const getUTMParamsFromURL = (): UTMParams => {
  const params: UTMParams = {};
  const urlParams = new URLSearchParams(window.location.search);
  
  // Check URL first, then fallback to sessionStorage
  const utmSource = urlParams.get('utm_source') || sessionStorage.getItem(UTM_STORAGE_KEYS.utm_source);
  const utmMedium = urlParams.get('utm_medium') || sessionStorage.getItem(UTM_STORAGE_KEYS.utm_medium);
  const utmCampaign = urlParams.get('utm_campaign') || sessionStorage.getItem(UTM_STORAGE_KEYS.utm_campaign);
  const utmAdset = urlParams.get('utm_adset') || sessionStorage.getItem(UTM_STORAGE_KEYS.utm_adset);
  const utmAd = urlParams.get('utm_ad') || sessionStorage.getItem(UTM_STORAGE_KEYS.utm_ad);
  const utmId = urlParams.get('utm_id') || sessionStorage.getItem(UTM_STORAGE_KEYS.utm_id);
  const utmContent = urlParams.get('utm_content') || sessionStorage.getItem(UTM_STORAGE_KEYS.utm_content);
  const utmTerm = urlParams.get('utm_term') || sessionStorage.getItem(UTM_STORAGE_KEYS.utm_term);
  const fbclid = urlParams.get('fbclid') || sessionStorage.getItem(UTM_STORAGE_KEYS.fbclid);
  const gclid = urlParams.get('gclid') || sessionStorage.getItem(UTM_STORAGE_KEYS.gclid);
  
  // Store in sessionStorage if found in URL (for subsequent pages)
  if (urlParams.get('utm_source')) sessionStorage.setItem(UTM_STORAGE_KEYS.utm_source, urlParams.get('utm_source')!);
  if (urlParams.get('utm_medium')) sessionStorage.setItem(UTM_STORAGE_KEYS.utm_medium, urlParams.get('utm_medium')!);
  if (urlParams.get('utm_campaign')) sessionStorage.setItem(UTM_STORAGE_KEYS.utm_campaign, urlParams.get('utm_campaign')!);
  if (urlParams.get('utm_adset')) sessionStorage.setItem(UTM_STORAGE_KEYS.utm_adset, urlParams.get('utm_adset')!);
  if (urlParams.get('utm_ad')) sessionStorage.setItem(UTM_STORAGE_KEYS.utm_ad, urlParams.get('utm_ad')!);
  if (urlParams.get('utm_id')) sessionStorage.setItem(UTM_STORAGE_KEYS.utm_id, urlParams.get('utm_id')!);
  if (urlParams.get('utm_content')) sessionStorage.setItem(UTM_STORAGE_KEYS.utm_content, urlParams.get('utm_content')!);
  if (urlParams.get('utm_term')) sessionStorage.setItem(UTM_STORAGE_KEYS.utm_term, urlParams.get('utm_term')!);
  if (urlParams.get('fbclid')) sessionStorage.setItem(UTM_STORAGE_KEYS.fbclid, urlParams.get('fbclid')!);
  if (urlParams.get('gclid')) sessionStorage.setItem(UTM_STORAGE_KEYS.gclid, urlParams.get('gclid')!);
  
  // Build params object
  if (utmSource) params.utm_source = utmSource;
  if (utmMedium) params.utm_medium = utmMedium;
  if (utmCampaign) params.utm_campaign = utmCampaign;
  if (utmAdset) params.utm_adset = utmAdset;
  if (utmAd) params.utm_ad = utmAd;
  if (utmId) params.utm_id = utmId;
  if (utmContent) params.utm_content = utmContent;
  if (utmTerm) params.utm_term = utmTerm;
  if (fbclid) params.fbclid = fbclid;
  if (gclid) params.gclid = gclid;
  
  return params;
};

/**
 * Get fbclid - first checks URL, then sessionStorage
 * This ensures fbclid is available on first page load and subsequent pages
 */
export const getFbclid = (): string | null => {
  // First check URL (for first page load)
  const urlParams = new URLSearchParams(window.location.search);
  const fbclidFromUrl = urlParams.get('fbclid');
  
  if (fbclidFromUrl) {
    // Store it for subsequent pages
    sessionStorage.setItem('fbclid', fbclidFromUrl);
    return fbclidFromUrl;
  }
  
  // Fallback to sessionStorage (for navigation to other pages)
  return sessionStorage.getItem('fbclid');
};

/**
 * Get gclid (Google Click ID) - first checks URL, then sessionStorage
 * This ensures gclid is available on first page load and subsequent pages
 */
export const getGclid = (): string | null => {
  // First check URL (for first page load)
  const urlParams = new URLSearchParams(window.location.search);
  const gclidFromUrl = urlParams.get('gclid');
  
  if (gclidFromUrl) {
    // Store it for subsequent pages
    sessionStorage.setItem('gclid', gclidFromUrl);
    return gclidFromUrl;
  }
  
  // Fallback to sessionStorage (for navigation to other pages)
  return sessionStorage.getItem('gclid');
};

/**
 * Extract and store titleId from URL
 * Used to redirect to Vertical TV website instead of Play Store on Subscribed page
 */
export const extractAndStoreTitleId = (): void => {
  const urlParams = new URLSearchParams(window.location.search);
  const titleId = urlParams.get('titleId');
  
  if (titleId) {
    sessionStorage.setItem('titleId', titleId);
  }
};

/**
 * Get stored titleId from sessionStorage
 */
export const getStoredTitleId = (): string | null => {
  return sessionStorage.getItem('titleId');
};

/**
 * Get Vertical TV redirect URL with titleId
 * Returns null if no titleId is stored
 */
export const getVerticalTVRedirectUrl = (): string | null => {
  const titleId = getStoredTitleId();
  if (titleId) {
    return `https://www.verticaltvbyzupee.com/?z=77&titleId=${titleId}`;
  }
  return null;
};

// ============================================
// Mobile Number Storage for CT Events
// After successful login, mobile is persisted and 
// automatically appended to all subsequent CT events
// ============================================

/**
 * Store mobile number after successful login
 * This is called after WEB_EVENT_LOGIN
 * @param mobile - User's mobile number
 */
export const storeUserMobile = (mobile: string): void => {
  if (mobile) {
    sessionStorage.setItem('ct_user_mobile', mobile);
    console.log('[CT] Mobile number stored for future events');
  }
};

/**
 * Get stored mobile number
 * Returns null if user hasn't logged in yet
 */
export const getStoredMobile = (): string | null => {
  return sessionStorage.getItem('ct_user_mobile');
};

/**
 * Clear stored mobile number (on logout)
 */
export const clearStoredMobile = (): void => {
  sessionStorage.removeItem('ct_user_mobile');
};

/**
 * Get titleId - first checks URL, then sessionStorage
 * This ensures titleId is available on first page load and subsequent pages
 */
export const getTitleId = (): string | null => {
  // First check URL (for first page load)
  const urlParams = new URLSearchParams(window.location.search);
  const titleIdFromUrl = urlParams.get('titleId');
  
  if (titleIdFromUrl) {
    // Store it for subsequent pages
    sessionStorage.setItem('titleId', titleIdFromUrl);
    return titleIdFromUrl;
  }
  
  // Fallback to sessionStorage (for navigation to other pages)
  return sessionStorage.getItem('titleId');
};
